import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { selectIsAuthenticated, selectClinicsLoading } from '@/store/slices/authSlice'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const clinicsLoading = useSelector(selectClinicsLoading)
  const location = useLocation()
  const [isInitializing, setIsInitializing] = useState(true)

  // Allow time for authentication state to be restored from sessionStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false)
    }, 100) // Short delay to allow HATEOASInitializer to restore auth state

    return () => clearTimeout(timer)
  }, [])

  // Store intended route in sessionStorage when not authenticated
  // MUST be called before any conditional returns to follow React hooks rules
  useEffect(() => {
    if (!isAuthenticated && !isInitializing) {
      const intendedRoute = location.pathname + location.search
      // Only store if it's not the login page itself
      if (intendedRoute !== '/' && intendedRoute !== '/signup' && intendedRoute !== '/onboarding') {
        sessionStorage.setItem('vitl_intended_route', intendedRoute)
        console.log('[ProtectedRoute] Stored intended route:', intendedRoute)
      }
    }
  }, [isAuthenticated, isInitializing, location.pathname, location.search])

  // Show loading while:
  // 1. Still initializing auth state
  // 2. Authenticated but clinics are still loading (need clinics for HATEOAS)
  if (isInitializing || (isAuthenticated && clinicsLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-vitl-green" />
          <p className="text-gray-600 font-lato">
            {isInitializing ? 'Initializing...' : 'Loading clinic data...'}
          </p>
        </div>
      </div>
    )
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  // User is authenticated and clinics are loaded, render the protected content
  return <>{children}</>
}

export default ProtectedRoute